import { useEffect, useReducer, useRef, useState } from "react";
import { useParams } from "react-router";
import { updateDataSet } from "../api/dataSetApiContext";
import { getInitialTabularState, reducer } from "../components/viewer/storage/runState.ts";
import { useEnhancedBiApiClientProvider } from "../contexts/ApiClientProviderContext";
import { useAppDispatch } from "../store/hooks/useAppDispatch";
import { setViewReport } from "../store/thunks/currentReportThunks";
import { createCurrentReportAction } from "../store/utils/storeUtils";
import { getExistingReportWithAuth, manageReportSwitching } from "../utilities/defineReportDataHelper";

const useDefineViewReportData = () => {
  const { id } = useParams();
  const { getReport, getReportAuthorizationInfo, getReportConfigurationForRunning } = useEnhancedBiApiClientProvider();
  const dispatch = useAppDispatch();
  const [showReportNotFound, setShowReportNotFound] = useState(false);
  const [notFoundReason, setNotFoundReason] = useState<string>();
  const [isSwitchingReport, setIsSwitchingReport] = useState(false);
  const cachedReportIdRef = useRef<string | undefined>(undefined);
  const [isFetching, setIsFetching] = useState(false);
  const [runState, dispatchRunState] = useReducer(reducer, getInitialTabularState());

  useEffect(() => {
    const isSwitchingReport = !!cachedReportIdRef.current && cachedReportIdRef.current !== id;
    cachedReportIdRef.current = id;

    let isCurrent = true;
    const execute = async () => {
      if (id !== undefined) {
        setIsFetching(true);
        await getExistingReportWithAuth(
          getReport.bind(null, id, "run"),
          getReportConfigurationForRunning.bind(null, id),
          getReportAuthorizationInfo.bind(null, id),
          (reason) => {
            if (isCurrent) {
              setNotFoundReason(reason);
              setShowReportNotFound(true);
            }
          },
          (report, runningData) => {
            if (!isCurrent) {
              return;
            }
            updateDataSet(runningData.dataSetId);
            dispatch(setViewReport({ ...report, dataSetId: runningData.dataSetId }));
            dispatchRunState({
              type: "Initialize",
              payload: { response: { ...runningData }, reportId: id },
            });
          },
          (authorizationInfo) =>
            isCurrent && dispatch(createCurrentReportAction("setAuthorizationInfo", authorizationInfo))
        );
        if (isCurrent) {
          setIsFetching(false);
        }
      }
    };
    manageReportSwitching(isSwitchingReport, setIsSwitchingReport, execute);

    return () => {
      isCurrent = false;
    };
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [id]);

  return { showReportNotFound, notFoundReason, isSwitchingReport, isFetching, runState, dispatchRunState };
};

export default useDefineViewReportData;
